/**
 * The entity-context payload — master spec §8a.2.
 *
 * `buildEntityContext(entityType, entityId, authContext)` backs the account-360
 * and opportunity-detail payloads rather than those assemblies being written
 * twice, which is why it earns its place in v1 regardless of the AI work it
 * was listed for.
 *
 * **Bounded query count regardless of timeline length**: the timeline is
 * assembled from a fixed number of statements and truncated, never fetched per
 * row.
 */

import { z } from 'zod';
import { closureRowSchema, historyEntrySchema } from './closures.js';
import { attachmentRowSchema } from './attachments.js';
import { dataEnvelope } from './http.js';
import { dateString, timestampString, uuid } from './fields.js';
import { opportunityRowSchema } from './opportunities.js';
import { actionOwnerTypeSchema } from './enums.js';

export const contextPersonSchema = z.object({
  contact_id: uuid,
  first_name: z.string(),
  last_name: z.string(),
  title: z.string().nullable(),
  /** The EFFECTIVE role: the opportunity override when set, else the contact's own. */
  buying_role: z.string(),
  influence_level: z.string(),
  disposition: z.string(),
  email: z.string().nullable(),
  is_channel: z.boolean(),
  /**
   * On the account 360 only (session 6): the account the contact belongs to,
   * which is a site's when the page is its parent's roll-up.
   */
  account_id: uuid.optional(),
  account_name: z.string().optional(),
});

export const contextOpenItemSchema = z.object({
  id: uuid,
  title: z.string(),
  status: z.string(),
  priority: z.string(),
  due_date: dateString.nullable(),
  /**
   * Required, not optional: the Actions tab splits *Ours* from *Waiting on the
   * customer* on `owner_type`, and a payload without it files every item under
   * *Ours* without any error (docs/bugs.md, 2026-10-10 session 1).
   */
  owner_type: actionOwnerTypeSchema,
  owner_user_id: uuid.nullable(),
  owner_contact_id: uuid.nullable(),
  opportunity_id: uuid.nullable(),
  account_id: uuid.nullable(),
  blocked_by_action_id: uuid.nullable(),
});

export const contextTimelineEntrySchema = historyEntrySchema.extend({
  source: z.enum(['stage', 'closure', 'meeting', 'note', 'action', 'audit']),
});

export const entityContextSchema = z.object({
  entityType: z.enum(['opportunity', 'account']),
  entityId: uuid,
  /** The record itself, shaped by `entityType`. */
  record: z.record(z.unknown()),
  /** Present for an account: every opportunity on it, roll-ups across all of them. */
  opportunities: z.array(opportunityRowSchema).optional(),
  rollup: z
    .object({
      open_count: z.number().int(),
      won_count: z.number().int(),
      open_annual_revenue_net: z.string(),
      won_annual_revenue_net: z.string(),
      weighted_revenue: z.string(),
      lifetime_value: z.string(),
    })
    .optional(),
  /**
   * Present for an account: its live direct children — its sites (session 6)
   * — by name. From the summary itself, not from a page of the account list,
   * which is capped and missed sites once there were more accounts than fit.
   */
  sites: z.array(z.object({ id: uuid, name: z.string() })).optional(),
  people: z.array(contextPersonSchema),
  openItems: z.array(contextOpenItemSchema),
  closures: z.array(closureRowSchema),
  timeline: z.array(contextTimelineEntrySchema),
  attachments: z.array(attachmentRowSchema),
  /** Set when the timeline was truncated, so the UI can say so rather than imply completeness. */
  timelineTruncatedAt: timestampString.nullable(),
  /**
   * §2: scoping governs write AFFORDANCES, not roll-up totals. The summary
   * returns every opportunity on the account; this says which of them the
   * caller may write.
   */
  canWrite: z.boolean(),
});

export type EntityContext = z.infer<typeof entityContextSchema>;
export const entityContextResponseSchema = dataEnvelope(entityContextSchema);
